import React, { useMemo } from "react";
import { filter, first, orderBy, take, takeRight } from "lodash";
import { SectionHeader } from "@/shared/ui/SectionHeader";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import {
  RadarChart,
  RadarSector,
  RadarGroup,
} from "@/shared/widgets/Charts/RadarChart";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ModalFileName } from "@/shared/modals";
import { DimensionContext } from "@/types/data";
import { useTranslation } from "react-i18next";
import { ValuesInsights } from "@/shared/widgets/InsightsSection/ValuesInsights";
import { getRadarChartData, getDimensionById } from "./utils/getChartData";
import { DimensionsChart } from "@/shared/charts";

interface ProfileValuesSectionProps {
  dimensions: DimensionWithScores[];
  context: DimensionContext;
  defaultPersonId?: number;
  isPdf?: boolean;
}

interface PrioritizedDimensions {
  high: DimensionWithScores[];
  low: DimensionWithScores[];
}

const SHOW_TOP_N_DIMENSIONS = 3;

export const getPrioritizedDimensions = (
  dimensions: DimensionWithScores[]
): PrioritizedDimensions => {
  const priorityList = orderBy(
    filter(dimensions, (d) => d.description && !!d.parent),
    "score.value",
    "desc"
  ) as DimensionWithScores[];

  return {
    high: take(priorityList, SHOW_TOP_N_DIMENSIONS),
    low: orderBy(
      takeRight(priorityList, SHOW_TOP_N_DIMENSIONS),
      "score.value",
      "asc"
    ),
  };
};

function ProfileValuesSection(props: ProfileValuesSectionProps) {
  const { dimensions, context, defaultPersonId, isPdf } = props;
  const [openValuesDimensionModal] = useModal(ModalFileName.DIMENSION_DETAILS);
  const { t } = useTranslation();

  // Transform dimensions data to RadarChart format
  const chartData = useMemo(
    () =>
      getRadarChartData({
        dimensions,
        t,
        personId: defaultPersonId,
        context,
      }),
    [dimensions, t]
  );

  const handleSectorClick = (sector: RadarSector, group: RadarGroup) => {
    const dimension = getDimensionById(dimensions, sector.id);
    if (dimension) {
      openValuesDimensionModal({
        dimension,
        allDimensions: filter(dimensions, (d) => d.parent !== null),
        context,
      });
    }
  };

  // Temporary Chart type check
  const useLegacyChart = first(dimensions)?.group === "values";

  return (
    <section>
      <SectionHeader
        title={t("profile.values")}
        subtitle={t("profile.values_description", {
          context: t(`context_label.${context}_posessive`),
        })}
      />
      <div className="h-[755px]">
        {useLegacyChart ? (
          <DimensionsChart
            dimensions={dimensions}
            onClickSector={handleSectorClick as any}
            defaultPersonId={defaultPersonId}
          />
        ) : (
          <RadarChart
            groups={chartData.groups}
            axisLabels={chartData.axisLabels}
            data={chartData.data}
            defaultScores={chartData.defaultScores}
            defaultActiveId={defaultPersonId}
            onSectorClick={handleSectorClick}
          />
        )}
      </div>
      <ValuesInsights
        dimensions={dimensions}
        context={context}
        defaultPersonId={defaultPersonId}
      />
    </section>
  );
}

export default ProfileValuesSection;
export { ProfileValuesSection };
